import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Plus, MoreVertical, Pencil, Trash2, ShoppingCart, Eye, Check, Package, X, 
  ChevronDown, ChevronRight, Clock, User, FileText, CheckCircle, Send, Truck,
  Paperclip, Upload, Download, File, Image, FileType
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Draft', class: 'bg-slate-100 text-slate-700', icon: FileText },
  requested: { label: 'Requested', class: 'bg-amber-100 text-amber-700', icon: Send },
  approved: { label: 'Approved', class: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  ordered: { label: 'Ordered', class: 'bg-indigo-100 text-indigo-700', icon: Package },
  shipped: { label: 'Shipped', class: 'bg-purple-100 text-purple-700', icon: Truck },
  received: { label: 'Received', class: 'bg-emerald-100 text-emerald-700', icon: Check },
  cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-700', icon: X },
};

const WorkflowTimeline = ({ order, formatCurrency }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const timelineSteps = [
    { 
      label: 'Created', 
      by: order.created_by, 
      at: order.created_at,
      completed: true 
    },
    { 
      label: 'Requested', 
      by: order.requested_by, 
      at: order.requested_at,
      completed: !!order.requested_at 
    },
    { 
      label: 'Approved', 
      by: order.approved_by, 
      at: order.approved_at,
      completed: !!order.approved_at 
    },
    { 
      label: 'Completed', 
      by: order.completed_by, 
      at: order.completed_at,
      completed: !!order.completed_at 
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-slate-500">Project</p>
          <p className="font-semibold">{order.project || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">Total</p>
          <p className="font-mono font-bold text-lg">{formatCurrency(order.total_amount)}</p>
        </div>
      </div>
      
      <div className="border-t pt-4">
        <p className="text-sm font-semibold text-slate-700 mb-3">Workflow Timeline</p>
        <div className="space-y-3">
          {timelineSteps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                step.completed ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
              }`}>
                {step.completed ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${step.completed ? 'text-slate-900' : 'text-slate-400'}`}>
                  {step.label}
                </p>
                {step.completed && (
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <p className="flex items-center gap-1">
                      <User className="w-3 h-3" /> {step.by || 'N/A'}
                    </p>
                    <p className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDate(step.at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {order.workflow_history && order.workflow_history.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Activity Log</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {order.workflow_history.map((entry, idx) => (
              <div key={idx} className="text-xs p-2 bg-slate-50 rounded border">
                <p className="font-medium capitalize">{entry.action}</p>
                <p className="text-slate-500">By: {entry.user_name}</p>
                <p className="text-slate-400">{formatDate(entry.timestamp)}</p>
                {entry.notes && <p className="text-slate-600 mt-1">{entry.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const { canEdit, canApprove, canDelete } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useSettings();

  const [formData, setFormData] = useState({
    supplier_id: '',
    location_id: '',
    status: 'draft',
    notes: '',
    expected_delivery: '',
    project: '',
    items: []
  });

  const [newItem, setNewItem] = useState({
    part_id: '',
    quantity: 1,
    unit_price: 0,
    is_new_item: false,
    part_reference: '',
    part_description: '',
    category_id: '',
    minimum_quantity: 10,
    selling_price: 0
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, locationsRes, partsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/categories`)
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setLocations(locationsRes.data);
      setParts(partsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      location_id: '',
      status: 'draft',
      notes: '',
      expected_delivery: '',
      project: '',
      items: []
    });
    resetNewItem();
    setEditingOrder(null);
  };

  const resetNewItem = () => {
    setNewItem({
      part_id: '',
      quantity: 1,
      unit_price: 0,
      is_new_item: false,
      part_reference: '',
      part_description: '',
      category_id: '',
      minimum_quantity: 10,
      selling_price: 0
    });
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        supplier_id: order.supplier_id,
        location_id: order.location_id,
        status: order.status,
        notes: order.notes || '',
        expected_delivery: order.expected_delivery || '',
        project: order.project || '',
        items: order.items || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleAddItem = () => {
    if (newItem.is_new_item) {
      // Adding a new item not in inventory
      if (!newItem.part_reference || !newItem.part_description) {
        toast.error('Reference and description are required for new items');
        return;
      }
      if (!newItem.category_id) {
        toast.error('Category is required for new items');
        return;
      }
      
      const item = {
        part_id: null,
        part_reference: newItem.part_reference,
        part_description: newItem.part_description,
        quantity: newItem.quantity,
        unit_price: newItem.unit_price,
        is_new_item: true,
        category_id: newItem.category_id,
        minimum_quantity: newItem.minimum_quantity,
        selling_price: newItem.selling_price || newItem.unit_price * 1.3
      };

      setFormData({
        ...formData,
        items: [...formData.items, item]
      });
    } else {
      // Adding existing part from inventory
      if (!newItem.part_id) {
        toast.error('Select a part');
        return;
      }
      const part = parts.find(p => p.id === newItem.part_id);
      if (!part) return;

      const item = {
        part_id: newItem.part_id,
        part_reference: part.reference_number,
        part_description: part.description,
        quantity: newItem.quantity,
        unit_price: newItem.unit_price || part.buying_price,
        is_new_item: false
      };

      setFormData({
        ...formData,
        items: [...formData.items, item]
      });
    }
    resetNewItem();
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  // Attachment handlers
  const handleAttachmentUpload = async (orderId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/purchase-orders/${orderId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Attachment uploaded');
      fetchData();
      // Update viewing order if open
      if (viewingOrder?.id === orderId) {
        const response = await axios.get(`${API}/purchase-orders`);
        const updated = response.data.find(o => o.id === orderId);
        if (updated) setViewingOrder(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    }
  };

  const handleAttachmentDelete = async (orderId, attachmentId) => {
    try {
      await axios.delete(`${API}/purchase-orders/${orderId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchData();
      // Update viewing order if open
      if (viewingOrder?.id === orderId) {
        const response = await axios.get(`${API}/purchase-orders`);
        const updated = response.data.find(o => o.id === orderId);
        if (updated) setViewingOrder(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    try {
      if (editingOrder) {
        await axios.put(`${API}/purchase-orders/${editingOrder.id}`, formData);
        toast.success('Purchase order updated');
      } else {
        await axios.post(`${API}/purchase-orders`, formData);
        toast.success('Purchase order created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order');
    }
  };

  const handleRequestApproval = async (orderId) => {
    try {
      await axios.post(`${API}/purchase-orders/${orderId}/request`);
      toast.success('Purchase order submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleApprove = async (orderId) => {
    try {
      await axios.post(`${API}/purchase-orders/${orderId}/approve`);
      toast.success('Purchase order approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/purchase-orders/${orderId}`, { status: newStatus });
      if (newStatus === 'received') {
        toast.success('Order received! Stock quantities updated.');
      } else {
        toast.success('Status updated');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/purchase-orders/${orderToDelete.id}`);
      toast.success('Purchase order deleted');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const toggleRowExpand = (orderId) => {
    setExpandedRows(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesSupplier = filterSupplier === 'all' || order.supplier_id === filterSupplier;
    return matchesStatus && matchesSupplier;
  });

  const totalValue = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Purchase Orders</h1>
          <p className="text-slate-500 mt-1">Track and manage supplier orders</p>
        </div>
        {canEdit() && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Order
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(statusConfig).map(([value, config]) => (
                  <SelectItem key={value} value={value}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSupplier} onValueChange={setFilterSupplier}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="supplier-filter">
                <SelectValue placeholder="Supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(sup => (
                  <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredOrders.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Order #</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map(order => (
                  <Collapsible key={order.id} open={expandedRows[order.id]} asChild>
                    <>
                      <TableRow data-testid={`order-row-${order.id}`}>
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleRowExpand(order.id)}
                            >
                              {expandedRows[order.id] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell className="font-mono font-semibold text-slate-900">
                          {order.order_number}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">
                          {order.project || <span className="text-slate-400">N/A</span>}
                        </TableCell>
                        <TableCell>{order.supplier_name}</TableCell>
                        <TableCell className="text-slate-600">{order.location_name}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(order.total_amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm ${statusConfig[order.status]?.class || ''}`}>
                            {statusConfig[order.status]?.label || order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setViewingOrder(order);
                                setViewDialogOpen(true);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              
                              {/* Draft → Request Approval */}
                              {canEdit() && order.status === 'draft' && (
                                <DropdownMenuItem onClick={() => handleRequestApproval(order.id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Submit for Approval
                                </DropdownMenuItem>
                              )}
                              
                              {/* Requested → Approve (Admin/SCM only) */}
                              {canApprove() && order.status === 'requested' && (
                                <DropdownMenuItem onClick={() => handleApprove(order.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                              )}
                              
                              {/* Approved → Ordered */}
                              {canEdit() && order.status === 'approved' && (
                                <DropdownMenuItem onClick={() => handleStatusChange(order.id, 'ordered')}>
                                  <Package className="w-4 h-4 mr-2" />
                                  Mark as Ordered
                                </DropdownMenuItem>
                              )}
                              
                              {/* Ordered → Shipped */}
                              {canEdit() && order.status === 'ordered' && (
                                <DropdownMenuItem onClick={() => handleStatusChange(order.id, 'shipped')}>
                                  <Truck className="w-4 h-4 mr-2" />
                                  Mark as Shipped
                                </DropdownMenuItem>
                              )}
                              
                              {/* Shipped → Received */}
                              {canEdit() && order.status === 'shipped' && (
                                <DropdownMenuItem onClick={() => handleStatusChange(order.id, 'received')}>
                                  <Check className="w-4 h-4 mr-2" />
                                  Mark Received
                                </DropdownMenuItem>
                              )}
                              
                              {/* Edit (only draft/requested) */}
                              {canEdit() && ['draft', 'requested'].includes(order.status) && (
                                <DropdownMenuItem onClick={() => handleOpenDialog(order)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              
                              {/* Cancel */}
                              {canEdit() && !['received', 'cancelled'].includes(order.status) && (
                                <DropdownMenuItem 
                                  className="text-orange-600"
                                  onClick={() => handleStatusChange(order.id, 'cancelled')}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </DropdownMenuItem>
                              )}
                              
                              {/* Delete (Admin/SCM only) */}
                              {canDelete() && !['received'].includes(order.status) && (
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => {
                                    setOrderToDelete(order);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableCell colSpan={9} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Items */}
                              <div>
                                <p className="text-sm font-semibold text-slate-700 mb-2">Order Items</p>
                                <div className="border rounded-sm divide-y bg-white">
                                  {order.items?.map((item, idx) => (
                                    <div key={idx} className={`p-2 flex justify-between items-center ${item.is_new_item ? 'bg-amber-50' : ''}`}>
                                      <div className="flex items-center gap-2">
                                        {item.is_new_item && (
                                          <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-sm">NEW</Badge>
                                        )}
                                        <div>
                                          <p className="font-mono text-sm font-semibold">{item.part_reference}</p>
                                          <p className="text-xs text-slate-500">{item.part_description}</p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm">{item.quantity} × {formatCurrency(item.unit_price)}</p>
                                        <p className="font-semibold">{formatCurrency(item.quantity * item.unit_price)}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Timeline */}
                              <div>
                                <WorkflowTimeline order={order} formatCurrency={formatCurrency} />
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <ShoppingCart className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No purchase orders</p>
              <p className="text-sm">Create your first purchase order</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold font-mono">{viewingOrder?.order_number}</DialogTitle>
            <DialogDescription>Purchase order details and workflow timeline</DialogDescription>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Supplier</p>
                  <p className="font-semibold">{viewingOrder.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Destination</p>
                  <p className="font-semibold">{viewingOrder.location_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`rounded-sm ${statusConfig[viewingOrder.status]?.class}`}>
                    {statusConfig[viewingOrder.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expected Delivery</p>
                  <p className="font-semibold">{viewingOrder.expected_delivery || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-500 mb-2">Items</p>
                <div className="border rounded-sm divide-y max-h-48 overflow-y-auto">
                  {viewingOrder.items?.map((item, idx) => (
                    <div key={idx} className="p-2 flex justify-between items-center">
                      <div>
                        <p className="font-mono text-sm font-semibold">{item.part_reference}</p>
                        <p className="text-xs text-slate-500">{item.part_description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">{item.quantity} × {formatCurrency(item.unit_price)}</p>
                        <p className="font-semibold">{formatCurrency(item.quantity * item.unit_price)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {viewingOrder.notes && (
                <div>
                  <p className="text-sm text-slate-500">Notes</p>
                  <p className="text-sm">{viewingOrder.notes}</p>
                </div>
              )}
              
              {/* Attachments Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Paperclip className="w-4 h-4" />
                    Attachments ({viewingOrder.attachments?.length || 0})
                  </p>
                  {canEdit() && viewingOrder.status === 'draft' && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => handleAttachmentUpload(viewingOrder.id, e.target.files[0])}
                      />
                      <span className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        <Upload className="w-3 h-3" />
                        Upload
                      </span>
                    </label>
                  )}
                </div>
                {(viewingOrder.attachments?.length > 0) ? (
                  <div className="border rounded-sm divide-y max-h-32 overflow-y-auto">
                    {viewingOrder.attachments.map((att) => (
                      <div key={att.id} className="p-2 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex items-center gap-2 min-w-0">
                          {att.file_type === 'pdf' && <FileType className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          {att.file_type === 'image' && <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                          {(att.file_type === 'doc' || att.file_type === 'docx') && <File className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                          {att.file_type === 'other' && <File className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{att.original_filename}</p>
                            <p className="text-xs text-slate-500">
                              {(att.file_size / 1024).toFixed(1)} KB • {att.uploaded_by}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => window.open(`${BASE_URL}${att.url}`, '_blank')}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          {canEdit() && viewingOrder.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                              onClick={() => handleAttachmentDelete(viewingOrder.id, att.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border rounded-sm p-4 text-center text-slate-400 text-sm">
                    No attachments
                  </div>
                )}
              </div>
              
              <WorkflowTimeline order={viewingOrder} formatCurrency={formatCurrency} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingOrder ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </DialogTitle>
            <DialogDescription>
              {editingOrder ? 'Update order details' : 'Create a new purchase order'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Project Field */}
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                placeholder="Enter project name"
                className="rounded-sm"
                data-testid="order-project-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="order-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destination Location *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="order-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Expected Delivery</Label>
              <Input
                type="date"
                value={formData.expected_delivery}
                onChange={(e) => setFormData({ ...formData, expected_delivery: e.target.value })}
                className="rounded-sm"
              />
            </div>

            {/* Add Items Section */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  <span>Order Items</span>
                  <label className="flex items-center gap-2 text-xs font-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newItem.is_new_item}
                      onChange={(e) => setNewItem({ ...newItem, is_new_item: e.target.checked, part_id: '' })}
                      className="rounded"
                    />
                    <span className="text-amber-600">Add new item (not in inventory)</span>
                  </label>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!newItem.is_new_item ? (
                  /* Existing Part Selection */
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 mb-1 block">Part</Label>
                      <Select
                        value={newItem.part_id}
                        onValueChange={(value) => {
                          const part = parts.find(p => p.id === value);
                          setNewItem({ ...newItem, part_id: value, unit_price: part?.buying_price || 0 });
                        }}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select part from inventory" />
                        </SelectTrigger>
                        <SelectContent>
                          {parts.map(part => (
                            <SelectItem key={part.id} value={part.id}>
                              {part.reference_number} - {part.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <Label className="text-xs text-slate-500 mb-1 block">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs text-slate-500 mb-1 block">Unit Price</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                        className="rounded-sm"
                        placeholder={getCurrencySymbol()}
                      />
                    </div>
                    <Button type="button" onClick={handleAddItem} size="sm" className="rounded-sm mb-0.5">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  /* New Item Form */
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm space-y-3">
                    <p className="text-xs text-amber-700 font-medium">
                      This item will be added to your inventory when the PO is received
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={newItem.part_reference}
                        onChange={(e) => setNewItem({ ...newItem, part_reference: e.target.value })}
                        placeholder="Reference # (e.g., EL-001)"
                        className="rounded-sm"
                      />
                      <Input
                        value={newItem.part_description}
                        onChange={(e) => setNewItem({ ...newItem, part_description: e.target.value })}
                        placeholder="Description"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Category *</Label>
                        <Select
                          value={newItem.category_id}
                          onValueChange={(value) => setNewItem({ ...newItem, category_id: value })}
                        >
                          <SelectTrigger className="rounded-sm">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Unit Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newItem.unit_price}
                          onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                          className="rounded-sm"
                          placeholder={getCurrencySymbol()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Min Qty</Label>
                        <Input
                          type="number"
                          min="0"
                          value={newItem.minimum_quantity}
                          onChange={(e) => setNewItem({ ...newItem, minimum_quantity: parseInt(e.target.value) || 10 })}
                          className="rounded-sm"
                        />
                      </div>
                    </div>
                    <Button type="button" onClick={handleAddItem} size="sm" className="w-full rounded-sm bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-2" /> Add New Item
                    </Button>
                  </div>
                )}

                {formData.items.length > 0 && (
                  <div className="border rounded-sm divide-y">
                    {formData.items.map((item, idx) => (
                      <div key={idx} className={`p-2 flex justify-between items-center ${item.is_new_item ? 'bg-amber-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          {item.is_new_item && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-sm">NEW</Badge>
                          )}
                          <div>
                            <p className="font-mono text-sm font-semibold">{item.part_reference}</p>
                            <p className="text-xs text-slate-500">{item.part_description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm">
                            {item.quantity} × {formatCurrency(item.unit_price)} = {formatCurrency(item.quantity * item.unit_price)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(idx)}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="p-2 bg-slate-50 flex justify-between font-semibold">
                      <span>Total</span>
                      <span className="font-mono">{formatCurrency(totalValue)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="order-submit-btn">
                {editingOrder ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Purchase Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete order "{orderToDelete?.order_number}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-order-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
